import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { JwtAuthService } from '../../../shared/services/auth/jwt-auth.service';
import { ProfileType } from '../../../shared/enums/profile-type.enum';
import { config } from 'config';
import {
  PerformerAddDialogComponent,
  PerformerDialogData,
} from './dialogs/performer-add-dialog.component';
import { AlertService } from 'app/shared/services/alert/alert.service';
import { REFERRAL_GROUPS, ReferralGroup } from '../../../shared/constants/referral-groups.constants';
import { ExportDialogComponent, ExportDialogData } from '../../../shared/components/export-dialog/export-dialog.component';
import { ExportService, ExportRequest } from '../../../shared/services/export.service';

export interface Performer {
  id: number;
  name: string;
  activeReferralGroupsID: string;
}

export interface PerformerResponse {
  current_page: number;
  data: Performer[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

@Component({
    selector: 'app-performer-maintenance',
    templateUrl: './performer-maintenance.component.html',
    styleUrls: ['./performer-maintenance.component.scss'],
    standalone: false
})
export class PerformerMaintenanceComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = ['name', 'actions'];

  dataSource = new MatTableDataSource<Performer>();
  isLoading = false;
  searchTerm = '';
  searchType = 'performer_name';
  selectedGroupFilter = '';
  isActivityCheck = false;

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  // Pagination
  totalItems = 0;
  pageSize = 50;
  currentPage = 1;
  pageSizeOptions = [50, 100, 150, 200, 250];

  // Activity Dialog
  showActivityDialog = false;
  selectedPerformer: Performer | null = null;

  // Role-based access control
  isRoleBasedUser = false;
  userReferralGroupId: number | null = null;
  canAddPerformer = true;
  canEditPerformer = true;
  canDeletePerformer = true;
  canExport = true;

  referralGroups: ReferralGroup[] = REFERRAL_GROUPS;

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private authService: JwtAuthService,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private alertService: AlertService,
    private exportService: ExportService
  ) {
    this.setupSearch();
  }

  ngOnInit(): void {
    this.searchType = 'performer_name';

    this.setupRoleBasedAccess();
    this.checkRouteMode();
    this.loadFacilities();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private setupSearch(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(searchTerm => {
        this.searchTerm = searchTerm;
        this.currentPage = 1;
        this.loadFacilities();
      });
  }

  checkRouteMode(): void {
    this.isActivityCheck = this.router.url.includes('activity-check');
    if (this.isActivityCheck && !this.isRoleBasedUser) {
      this.displayedColumns = this.displayedColumns.filter(col => col !== 'group');
    }
  }

  setupRoleBasedAccess(): void {
    const userRole = this.authService.getRole();
    const user = this.authService.getUser();

    this.isRoleBasedUser = userRole !== ProfileType.Admin && userRole !== ProfileType.SuperAdmin;

    // Set permissions based on role
    switch (userRole) {
      case ProfileType.Admin:
      case ProfileType.SuperAdmin:
        this.canAddPerformer = true;
        this.canEditPerformer = true;
        this.canDeletePerformer = true;
        this.canExport = true;
        break;
      case ProfileType.RadiologyUser:
        this.canAddPerformer = false;
        this.canEditPerformer = false;
        this.canDeletePerformer = true;
        this.canExport = true;
        break;
      default:
        this.canAddPerformer = true; //false;
        this.canEditPerformer = true; //false;
        this.canDeletePerformer = true; //false;
        this.canExport = false;
    }

    if (this.authService.isRadiology()) {
      this.userReferralGroupId = this.authService.getReferralGroupId();
    }

    // If role-based user, set group filter and modify columns
    if (this.isRoleBasedUser && this.userReferralGroupId) {
      this.selectedGroupFilter = this.userReferralGroupId.toString();
      this.displayedColumns = this.displayedColumns.filter(col => col !== 'group');
    }
  }

  onSearchTypeChange(event?: any): void {
    if (event?.value) {
      this.searchType = event.value;
    }

    // Clear search when switching types
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadFacilities();
  }

  loadFacilities(): void {
    if (!this.selectedGroupFilter && !this.isRoleBasedUser) return;
    this.isLoading = true;

    let url = `${config.apiUrl1}performer/get?page=${this.currentPage}&per_page=${this.pageSize}`;

    if (this.selectedGroupFilter) {
      url += `&referral_group_id=${this.selectedGroupFilter}`;

      this.userReferralGroupId = parseInt(this.selectedGroupFilter, 10);
    }

    if (this.searchTerm) {
      url += `&search_term=${encodeURIComponent(this.searchTerm)}`;
      url += `&search_type=${this.searchType}`;
    }

    this.http.get<PerformerResponse>(url).subscribe({
      next: (response: PerformerResponse) => {
        // Clean clinic names
        const cleanedData = response.data.map(performer => ({
          ...performer,
        }));

        this.dataSource.data = cleanedData;
        this.totalItems = response.total;
        this.isLoading = false;
      },
      error: error => {
        this.isLoading = false;
        this.alertService.notifyError('Error loading facilities');
        console.error('Error loading facilities:', error);
      },
    });
  }

  onSearchInput(event: any): void {
    this.searchSubject.next(event.target.value);
  }

  applyFilter(): void {
    this.currentPage = 1;
    this.loadFacilities();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadFacilities();
  }

  deletePerformer(performer: Performer): void {
    const confirmMessage = `Are you sure you want to delete performer "${performer.name}" (${performer.id})?`;

    if (confirm(confirmMessage)) {
      const url = `${config.apiUrl1}performer/delete/${performer.id}`;
      const body = {
        referral_group_id: this.userReferralGroupId
      };

      this.http.post(url, body).subscribe({
        next: () => {
          this.alertService.notifySuccess('Performer deleted successfully');
          this.loadFacilities();
        },
        error: error => {
          this.alertService.notifyError('Error deleting performer');
          console.error('Error deleting performer:', error);
        },
      });
    }
  }

  closeActivityDialog(): void {
    this.showActivityDialog = false;
    this.selectedPerformer = null;
  }

  exportToCSV(): void {
    const csvData = this.dataSource.data.map(performer => ({
      'Performer Name': performer.name,
      'Referral Group': this.getReferralGroupName(this.userReferralGroupId || performer.activeReferralGroupsID),
    }));

    const csvContent = this.convertToCSV(csvData);
    const filename = `performers_${new Date().toISOString().split('T')[0]}.csv`;
    this.downloadCSV(csvContent, filename);
    this.alertService.notifySuccess('Performer exported successfully');
  }

  private convertToCSV(data: any[]): string {
    if (data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const rows = data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','));

    return [headers.join(','), ...rows].join('\n');
  }

  private downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  refreshData(): void {
    this.loadFacilities();
  }

  getReferralGroupName(groupId: string | number): string {
    const id = typeof groupId === 'string' ? parseInt(groupId) : groupId;
    const group = this.referralGroups.find(g => g.value === id);
    return group ? group.label : 'Unknown';
  }

  cleanClinicName(name: string): string {
    if (!name) return '';
    return name.replace(/\t/g, ' ').replace(/\s+/g, ' ').trim();
  }

  trackByPerformerId(index: number, performer: Performer): number {
    return performer.id;
  }

  Math = Math; // Expose Math to template

  editPerformer(performer: Performer): void {
    const dialogData: PerformerDialogData = {
      performer: performer,
      referralGroups: this.referralGroups,
      isEdit: true,
      isRoleBasedUser: this.isRoleBasedUser,
      userReferralGroupId: this.userReferralGroupId,
      userReferralGroupName: this.authService.isRadiology() ? this.authService.getReferralGroupName() : null,
    };

    const dialogRef = this.dialog.open(PerformerAddDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true,
      autoFocus: true,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.updatePerformer(result);
      }
    });
  }

  openAddPerformerDialog(): void {
    const dialogData: PerformerDialogData = {
      performer: null,
      referralGroups: this.referralGroups,
      isEdit: false,
      isRoleBasedUser: this.isRoleBasedUser,
      userReferralGroupId: this.userReferralGroupId,
      userReferralGroupName: this.authService.isRadiology() ? this.authService.getReferralGroupName() : null,
    };

    const dialogRef = this.dialog.open(PerformerAddDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true,
      autoFocus: true,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.createPerformer(result);
      }
    });
  }

  getGroupColor(groupId: string | number): string {
    const colors = [
      '#1976d2', // Blue
      '#388e3c', // Green
      '#f57c00', // Orange
      '#7b1fa2', // Purple
      '#d32f2f', // Red
      '#455a64', // Blue Grey
      '#689f38', // Light Green
      '#c2185b', // Pink
    ];
    const id = typeof groupId === 'string' ? parseInt(groupId) : groupId;
    return colors[(id - 1) % colors.length] || '#1976d2';
  }

  private createPerformer(performerData: any): void {
    this.isLoading = true;

    const payload = {
      performer_name: performerData.name,
      referral_group_id: performerData.referralGroup,
      practice_software: performerData.practiceSoftware,
      emails: performerData.emails,
    };

    const url = `${config.apiUrl1}performer/store`;

    this.http.post<any>(url, payload).subscribe({
      next: response => {
        this.isLoading = false;

        let successMsg = response.message || 'Performer created successfully';

        if (response.email_error && response.email_error.length > 0) {
          successMsg += `. However, some emails failed: ${response.email_error.join(', ')}`;
        }

        this.alertService.notifySuccess(successMsg);
        this.loadFacilities(); // Refresh the table
      },
      error: error => {
        this.isLoading = false;
        console.error('Error creating performer:', error);

        if (error.error?.message) {
          this.alertService.notifyError(`Error creating performer: ${error.error.message}`);
        } else if (error.error?.errors) {
          const errors = error.error.errors;
          const errorMessages: string[] = [];

          for (const key in errors) {
            if (errors.hasOwnProperty(key)) {
              const value = errors[key];
              if (Array.isArray(value)) {
                errorMessages.push(...value);
              } else if (typeof value === 'string') {
                errorMessages.push(value);
              }
            }
          }

          this.alertService.notifyError(`Validation errors: ${errorMessages.join(', ')}`);
        } else {
          this.alertService.notifyError('Error creating performer. Please try again.');
        }
      },
    });
  }

  private updatePerformer(performerData: any): void {
    this.isLoading = true;

    const payload = {
      performer_name: performerData.name,
      referral_group_id: performerData.referral_group_id
    };

    const url = `${config.apiUrl1}performer/update/${performerData.id}`;

    this.http.post<any>(url, payload).subscribe({
      next: response => {
        this.isLoading = false;
        this.alertService.notifySuccess('Performer updated successfully');
        this.loadFacilities(); // Refresh the table
      },
      error: error => {
        this.isLoading = false;
        console.error('Error updating performer:', error);

        // Handle specific error messages from the API
        if (error.error?.message) {
          this.alertService.notifyError(`Error updating performer: ${error.error.message}`);
        } else if (error.error?.errors) {
          // Handle validation errors
          const errors = error.error.errors;
          const errorMessages: string[] = [];

          for (const key in errors) {
            if (errors.hasOwnProperty(key)) {
              const value = errors[key];
              if (Array.isArray(value)) {
                errorMessages.push(...value);
              } else if (typeof value === 'string') {
                errorMessages.push(value);
              }
            }
          }
          this.alertService.notifyError(`Validation errors: ${errorMessages.join(', ')}`);
        } else {
          this.alertService.notifyError('Error updating performer. Please try again.');
        }
      },
    });
  }

  // Export functionality
  openExportDialog(): void {
    const dialogData: ExportDialogData = {
      title: 'Performer Maintenance',
      type: 'performer',
      referral_group_id: this.selectedGroupFilter || (this.userReferralGroupId ? this.userReferralGroupId.toString() : undefined),
      search_term: this.searchTerm || undefined,
      search_type: this.searchType || undefined
    };

    Object.keys(dialogData).forEach(key => {
      if (dialogData[key] === undefined || dialogData[key] === '') {
        delete dialogData[key];
      }
    });


    const dialogRef = this.dialog.open(ExportDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: false
    });

    // No need to handle the result since the API call happens inside the dialog
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
      }
    });
  }
}
